import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import db from '../../db/db';
import TicketVuelta from './TicketVuelta';

const Vueltas = () => {
    const navigate = useNavigate();

    const [vueltas, setVueltas]               = useState([]);
    const [empleados, setEmpleados]           = useState([]);
    const [eventos, setEventos]               = useState([]);
    const [vueltaACancelar, setVueltaACancelar] = useState(null);
    const [statusCancelar, setStatusCancelar] = useState(false);
    const [ticketVuelta, setTicketVuelta] = useState(null);
    const [sucursalNombre, setSucursalNombre] = useState('');

    const cargarVueltas = useCallback(async () => {
        const sesion = await db.sesion.toArray();
        const sucursalId = sesion[0]?.sucursal?._id || sesion[0]?.sucursal;
        const [vs, emps, evts, sucursales] = await Promise.all([
            db.vueltas.filter(v => String(v.sucursal?._id || v.sucursal) === String(sucursalId)).toArray(),
            db.empleados.toArray(),
            db.eventos.toArray(),
            db.sucursales.toArray(),
        ]);
        const sucReg = sucursales.find(s => String(s._id || s.id) === String(sucursalId));
        setSucursalNombre(sucReg?.nombre || '');
        setEmpleados(emps);
        setEventos(evts);
        setVueltas(vs.sort((a, b) => new Date(b.fecha) - new Date(a.fecha)));
    }, []);

    useEffect(() => { cargarVueltas(); }, [cargarVueltas]);

    const resolverEmpleado = (id) => {
        if (!id) return '—';
        const emp = empleados.find(x => x._id === id || String(x.id) === String(id));
        if (!emp) return '—';
        return `${emp.nombre} ${emp.apellido_paterno || ''}`.trim();
    };

    const resolverEvento = (id) => {
        if (!id) return '—';
        const item = eventos.find(x => x._id === id || String(x.id) === String(id));
        return item?.nombre || '—';
    };

    const handleConfirmarCancelacion = async () => {
        if (!vueltaACancelar) return;
        setStatusCancelar(true);
        try {
            await db.vueltas.update(vueltaACancelar.id, { cancelada: true });
            await cargarVueltas();
            setVueltaACancelar(null);
        } finally {
            setStatusCancelar(false);
        }
    };

    const handleImprimirVuelta = async (row) => {
        const [prods, catalogo] = await Promise.all([
            db.vueltas_productos.where('vuelta_local_id').equals(row.id).toArray(),
            db.productos.toArray(),
        ]);
        const carrito = prods.map(p => {
            const prod = catalogo.find(c => c._id === p.producto || String(c.id) === String(p.producto));
            return {
                nombre:       prod?.nombre || '—',
                cantidad:     (p.cantidad || 0) - (p.cantidad_regresada || 0),
                precio_total: p.precio,
                extras:       [],
            };
        }).filter(p => p.cantidad > 0);
        setTicketVuelta({
            folio:       row.folio,
            fecha:       new Date(row.fecha),
            vendedor:    resolverEmpleado(row.vendedor),
            responsable: resolverEmpleado(row.responsable),
            sucursal:    sucursalNombre,
            carrito,
            total:       row.total || 0,
        });
    };

    const columns = [
        {
            name: 'Folio',
            selector: row => row.folio,
            sortable: true,
            grow: 0.5,
            cell: row => (
                <span style={{ color: row.cancelada ? '#555' : '#F5C800', fontWeight: 700, fontFamily: 'monospace', textDecoration: row.cancelada ? 'line-through' : 'none' }}>
                    {row.folio || '—'}
                </span>
            ),
        },
        {
            name: 'Fecha',
            selector: row => row.fecha,
            sortable: true,
            grow: 1,
            cell: row => {
                const d = new Date(row.fecha);
                return (
                    <span style={{ color: row.cancelada ? '#444' : '#C0C0C0', fontSize: '0.85rem' }}>
                        {d.toLocaleDateString('es-MX')} {d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                );
            },
        },
        {
            name: 'Total',
            selector: row => row.total,
            sortable: true,
            grow: 0.5,
            cell: row => (
                <span style={{ color: row.cancelada ? '#555' : '#F0F0F0', fontWeight: 700 }}>
                    ${(row.total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
            ),
        },
        {
            name: 'Status',
            selector: row => row.cancelada ? 'cancelada' : row.status ? 'finalizada' : 'pendiente',
            sortable: true,
            grow: 1,
            cell: row => {
                if (row.cancelada) return <span style={badges.cancelada}>Cancelada</span>;
                if (row.status)    return <span style={badges.finalizada}>Finalizada</span>;
                return <span style={badges.pendiente}>Pendiente</span>;
            },
        },
        {
            name: 'Vendedor',
            selector: row => resolverEmpleado(row.vendedor),
            sortable: true,
            grow: 1,
            cell: row => <span style={{ color: row.cancelada ? '#444' : '#C0C0C0' }}>{resolverEmpleado(row.vendedor)}</span>,
        },
        {
            name: 'Responsable',
            selector: row => resolverEmpleado(row.responsable),
            sortable: true,
            grow: 1,
            cell: row => <span style={{ color: row.cancelada ? '#444' : '#C0C0C0' }}>{resolverEmpleado(row.responsable)}</span>,
        },
        {
            name: 'Evento',
            selector: row => resolverEvento(row.evento),
            sortable: true,
            grow: 1,
            cell: row => <span style={{ color: row.cancelada ? '#444' : '#C0C0C0' }}>{resolverEvento(row.evento)}</span>,
        },
        {
            name: '',
            width: '100px',
            cell: row => {
                if (row.cancelada) return (
                    <span style={{ ...styles.btnDisabled, borderColor: 'rgba(229,62,62,0.2)', color: '#5a2020' }} title="Venta cancelada">
                        <i className="fas fa-ban"></i>
                    </span>
                );
                if (row.status) return (
                    <button
                        style={styles.btnPrint}
                        title="Reimprimir ticket"
                        onClick={() => handleImprimirVuelta(row)}
                    >
                        <i className="fas fa-print"></i>
                    </button>
                );
                return (
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button
                            style={styles.btnComplementar}
                            title="Complementar venta"
                            onClick={() => navigate(`/complementar/${row.id}`)}
                        >
                            <i className="fas fa-arrow-right"></i>
                        </button>
                        <button
                            style={styles.btnCancelar}
                            title="Cancelar venta"
                            onClick={() => setVueltaACancelar(row)}
                        >
                            <i className="fas fa-ban"></i>
                        </button>
                    </div>
                );
            },
        },
    ];

    return (
        <>
        {ticketVuelta && (
            <TicketVuelta data={ticketVuelta} onCerrar={() => setTicketVuelta(null)} />
        )}
        <div style={styles.page}>
            <div style={styles.header}>
                <i className="fas fa-rotate-left me-3" style={{ color: '#F5C800', fontSize: '1.3rem' }}></i>
                <h4 style={styles.titulo}>Historial de ventas ambulantes</h4>
            </div>

            <div style={styles.tableWrap}>
                <DataTable
                    columns={columns}
                    data={vueltas}
                    pagination
                    paginationPerPage={15}
                    paginationRowsPerPageOptions={[10, 15, 25, 50]}
                    noDataComponent={
                        <div style={styles.vacio}>
                            <i className="fas fa-rotate-left" style={{ fontSize: '2rem', color: '#333', marginBottom: '0.75rem' }}></i>
                            <p style={{ color: '#888', margin: 0 }}>No hay vueltas registradas</p>
                        </div>
                    }
                    customStyles={tableStyles}
                    defaultSortFieldId={2}
                    defaultSortAsc={false}
                    dense
                />
            </div>

            {/* Modal confirmación cancelación */}
            {vueltaACancelar && (
                <div style={modal.overlay} onClick={() => !statusCancelar && setVueltaACancelar(null)}>
                    <div style={modal.card} onClick={e => e.stopPropagation()}>
                        <div style={modal.icon}>
                            <i className="fas fa-ban" style={{ color: '#E53E3E', fontSize: '2rem' }}></i>
                        </div>
                        <h5 style={modal.titulo}>¿Cancelar venta ambulante?</h5>
                        <p style={modal.texto}>
                            La venta con folio <strong style={{ color: '#F5C800' }}>{vueltaACancelar.folio}</strong> será marcada como cancelada y no se incluirá en la liquidación ni en la sincronización.
                            Esta acción no se puede deshacer.
                        </p>
                        <div style={modal.botones}>
                            <button
                                style={modal.btnNo}
                                onClick={() => setVueltaACancelar(null)}
                                disabled={statusCancelar}
                            >
                                No, mantener
                            </button>
                            <button
                                style={statusCancelar ? { ...modal.btnSi, opacity: 0.6, cursor: 'not-allowed' } : modal.btnSi}
                                onClick={handleConfirmarCancelacion}
                                disabled={statusCancelar}
                            >
                                {statusCancelar ? (
                                    <><span className="spinner-border spinner-border-sm me-2" role="status" />Cancelando...</>
                                ) : (
                                    'Sí, cancelar'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </>
    );
};

const badges = {
    finalizada: {
        background: 'rgba(245,200,0,0.12)',
        color: '#F5C800',
        border: '1px solid rgba(245,200,0,0.3)',
        borderRadius: '20px',
        padding: '0.2rem 0.65rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
    pendiente: {
        background: 'rgba(237,137,54,0.12)',
        color: '#ED8936',
        border: '1px solid rgba(237,137,54,0.35)',
        borderRadius: '20px',
        padding: '0.2rem 0.65rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
    cancelada: {
        background: 'rgba(229,62,62,0.1)',
        color: '#E53E3E',
        border: '1px solid rgba(229,62,62,0.3)',
        borderRadius: '20px',
        padding: '0.2rem 0.65rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
};

const styles = {
    page: {
        maxWidth: '1400px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '1.5rem',
        paddingBottom: '1rem',
        borderBottom: '1px solid #2E2E2E',
    },
    titulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        margin: 0,
    },
    tableWrap: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        overflow: 'hidden',
    },
    vacio: {
        padding: '3rem',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    btnComplementar: {
        background: 'transparent',
        border: '1px solid #2E2E2E',
        borderRadius: '7px',
        padding: '0.3rem 0.55rem',
        color: '#555',
        fontSize: '0.85rem',
        cursor: 'pointer',
    },
    btnCancelar: {
        background: 'transparent',
        border: '1px solid rgba(229,62,62,0.3)',
        borderRadius: '7px',
        padding: '0.3rem 0.55rem',
        color: '#E53E3E',
        fontSize: '0.85rem',
        cursor: 'pointer',
    },
    btnPrint: {
        background: 'transparent',
        border: '1px solid rgba(245,200,0,0.3)',
        borderRadius: '7px',
        padding: '0.3rem 0.55rem',
        color: '#F5C800',
        fontSize: '0.85rem',
        cursor: 'pointer',
    },
    btnDisabled: {
        display: 'inline-block',
        border: '1px solid #1E1E1E',
        borderRadius: '7px',
        padding: '0.3rem 0.55rem',
        color: '#333',
        fontSize: '0.85rem',
    },
};

const modal = {
    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem',
    },
    card: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '16px',
        padding: '2rem',
        maxWidth: '400px',
        width: '100%',
    },
    icon: {
        textAlign: 'center',
        marginBottom: '1rem',
    },
    titulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        textAlign: 'center',
        marginBottom: '0.75rem',
    },
    texto: {
        color: '#888',
        textAlign: 'center',
        fontSize: '0.95rem',
        marginBottom: '1.5rem',
        lineHeight: 1.6,
    },
    botones: {
        display: 'flex',
        gap: '0.75rem',
    },
    btnNo: {
        flex: 1,
        background: 'transparent',
        border: '1px solid #2E2E2E',
        borderRadius: '10px',
        padding: '0.8rem',
        color: '#888',
        fontWeight: 700,
        fontSize: '0.95rem',
        cursor: 'pointer',
    },
    btnSi: {
        flex: 1,
        background: '#E53E3E',
        border: 'none',
        borderRadius: '10px',
        padding: '0.8rem',
        color: '#fff',
        fontWeight: 800,
        fontSize: '0.95rem',
        cursor: 'pointer',
    },
};

const tableStyles = {
    table: { style: { background: '#1A1A1A' } },
    headRow: { style: { background: '#111111', borderBottomColor: '#2E2E2E' } },
    headCells: { style: { color: '#888888', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' } },
    rows: { style: { background: '#1A1A1A', borderBottomColor: '#222222', minHeight: '52px', '&:hover': { background: '#1F1F1F' } } },
    pagination: { style: { background: '#111111', borderTopColor: '#2E2E2E', color: '#888' } },
    noData: { style: { background: '#1A1A1A' } },
};

export default Vueltas;
